import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, 
  Package, 
  Truck, 
  MapPin, 
  Loader2
} from 'lucide-react';
import { calculateShipping, calculateTax, SALES_TAX_RATE } from '@/data/shipping';

const ADMIN_EMAIL = "joseph@josephgravesjr.com";

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, subtotal } = useCart();

  const [deliveryMethod, setDeliveryMethod] = useState('pickup'); // 'pickup' or 'ship'
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [shippingInfo, setShippingInfo] = useState({
    fullName: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });
  
  const [errors, setErrors] = useState({});

  // Calculate totals
  const shippingCost = deliveryMethod === 'ship' ? calculateShipping(items) : 0;
  const taxAmount = calculateTax(subtotal);
  const orderTotal = subtotal + shippingCost + taxAmount;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (deliveryMethod === 'ship') {
      if (!shippingInfo.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!shippingInfo.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(shippingInfo.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!shippingInfo.address.trim()) newErrors.address = 'Address is required';
      if (!shippingInfo.city.trim()) newErrors.city = 'City is required';
      if (!shippingInfo.state.trim()) newErrors.state = 'State is required';
      if (!shippingInfo.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    } else {
      // For pickup, still need email for confirmation
      if (!shippingInfo.email.trim()) {
        newErrors.email = 'Email is required for order confirmation';
      } else if (!/\S+@\S+\.\S+/.test(shippingInfo.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!shippingInfo.fullName.trim()) newErrors.fullName = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitOrder = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Determine the payment URL to redirect to.
      // If every cart item shares the same custom paymentUrl, use it.
      // Otherwise fall back to the general studio checkout URL.
      const uniquePaymentUrls = [...new Set(
        items.map(i => i.product.paymentUrl).filter(Boolean)
      )];
      const resolvedPaymentUrl =
        uniquePaymentUrls.length === 1 && uniquePaymentUrls[0] &&
        items.every(i => i.product.paymentUrl === uniquePaymentUrls[0])
          ? uniquePaymentUrls[0]
          : null; // null = use default in confirmation page

      // Prepare order data
      const orderData = {
        items: items.map(item => ({
          id: item.product.id,
          title: item.product.title,
          price: item.unitPrice ?? item.product.price,
          quantity: item.quantity,
          size: item.selectedSize || null,
          image: item.selectedImage || item.product.image,
          dedication: item.dedication || null,
          note: item.note || null
        })),
        deliveryMethod,
        shippingInfo: deliveryMethod === 'ship' ? shippingInfo : {
          fullName: shippingInfo.fullName,
          email: shippingInfo.email
        },
        subtotal,
        shippingCost,
        taxAmount,
        orderTotal,
        paymentUrl: resolvedPaymentUrl,
        adminEmail: ADMIN_EMAIL
      };
      
      // Send order to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/orders/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      let savedOrder = null;
      if (response.ok) {
        savedOrder = await response.json();
      }

      // Build the order snapshot we pass to the confirmation page
      const orderForConfirmation = {
        id: savedOrder?.id || `tmp-${Date.now()}`,
        ...orderData,
      };

      navigate('/order-confirmation', {
        state: { order: orderForConfirmation },
        replace: true,
      });

    } catch (error) {
      console.error('Error submitting order:', error);
      // Recompute payment URL for the fallback path too
      const uniqueUrlsFallback = [...new Set(
        items.map(i => i.product.paymentUrl).filter(Boolean)
      )];
      const resolvedPaymentUrlFallback =
        uniqueUrlsFallback.length === 1 && uniqueUrlsFallback[0] &&
        items.every(i => i.product.paymentUrl === uniqueUrlsFallback[0])
          ? uniqueUrlsFallback[0]
          : null;

      // Still proceed to confirmation so user can complete payment
      const orderForConfirmation = {
        id: `tmp-${Date.now()}`,
        items: items.map(item => ({
          id: item.product.id,
          title: item.product.title,
          price: item.unitPrice ?? item.product.price,
          quantity: item.quantity,
          size: item.selectedSize || null,
          image: item.selectedImage || item.product.image,
          dedication: item.dedication || null,
          note: item.note || null
        })),
        deliveryMethod,
        shippingInfo,
        subtotal,
        shippingCost,
        taxAmount,
        orderTotal,
        paymentUrl: resolvedPaymentUrlFallback,
      };
      navigate('/order-confirmation', {
        state: { order: orderForConfirmation },
        replace: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if cart is empty
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8">
        <Package className="w-16 h-16 text-muted-foreground/30 mb-4" />
        <h2 className="text-2xl font-display text-foreground mb-2">Your cart is empty</h2>
        <p className="text-muted-foreground mb-6">Add some items to continue</p>
        <Button onClick={() => navigate('/')} variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Return to Gallery
        </Button>
      </div>
    );
  }

  // Order submitted - handled by /order-confirmation route (navigate happens in handleSubmitOrder)

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="touch-target rounded-full hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-foreground" />
          </button>
          <h1 className="text-2xl font-display text-foreground">Checkout</h1>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-3 space-y-8">
            {/* Delivery Method */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-display text-foreground mb-4">Delivery Method</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Local Pickup */}
                <button
                  onClick={() => setDeliveryMethod('pickup')}
                  data-testid="delivery-method-pickup"
                  className={cn(
                    "flex flex-col items-center p-6 rounded-lg border-2 transition-all",
                    deliveryMethod === 'pickup'
                      ? "border-accent bg-accent/5"
                      : "border-border hover:border-slate-muted"
                  )}
                >
                  <MapPin className={cn(
                    "w-8 h-8 mb-3",
                    deliveryMethod === 'pickup' ? "text-accent" : "text-muted-foreground"
                  )} />
                  <span className="font-medium text-foreground">Local Pickup</span>
                  <span className="text-sm text-green-600 mt-1">Free</span>
                </button>
                
                {/* Ship to Me */}
                <button
                  onClick={() => setDeliveryMethod('ship')}
                  data-testid="delivery-method-ship"
                  className={cn(
                    "flex flex-col items-center p-6 rounded-lg border-2 transition-all",
                    deliveryMethod === 'ship'
                      ? "border-accent bg-accent/5"
                      : "border-border hover:border-slate-muted"
                  )}
                >
                  <Truck className={cn(
                    "w-8 h-8 mb-3",
                    deliveryMethod === 'ship' ? "text-accent" : "text-muted-foreground"
                  )} />
                  <span className="font-medium text-foreground">Ship to Me</span>
                  <span className="text-sm text-muted-foreground mt-1">Flat rate shipping</span>
                </button>
              </div>
            </div>
            
            {/* Contact & Shipping Info */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-display text-foreground mb-4">
                {deliveryMethod === 'ship' ? 'Shipping Information' : 'Contact Information'}
              </h2>
              
              <div className="space-y-4">
                {/* Full Name */}
                <div>
                  <Label htmlFor="fullName" className="text-sm text-foreground">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={shippingInfo.fullName}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className={cn(
                      "mt-1.5",
                      errors.fullName && "border-destructive"
                    )}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-destructive mt-1">{errors.fullName}</p>
                  )}
                </div>
                
                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-sm text-foreground">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={shippingInfo.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className={cn(
                      "mt-1.5",
                      errors.email && "border-destructive"
                    )}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1">{errors.email}</p>
                  )}
                </div>
                
                {/* Shipping fields - only show if Ship to Me */}
                {deliveryMethod === 'ship' && (
                  <>
                    {/* Address */}
                    <div>
                      <Label htmlFor="address" className="text-sm text-foreground">Shipping Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={shippingInfo.address}
                        onChange={handleInputChange}
                        placeholder="123 Main Street"
                        className={cn(
                          "mt-1.5",
                          errors.address && "border-destructive"
                        )}
                      />
                      {errors.address && (
                        <p className="text-xs text-destructive mt-1">{errors.address}</p>
                      )}
                    </div>
                    
                    {/* City, State, ZIP */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <Label htmlFor="city" className="text-sm text-foreground">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={shippingInfo.city}
                          onChange={handleInputChange}
                          placeholder="Denver"
                          className={cn(
                            "mt-1.5",
                            errors.city && "border-destructive"
                          )}
                        />
                        {errors.city && (
                          <p className="text-xs text-destructive mt-1">{errors.city}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="state" className="text-sm text-foreground">State *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={shippingInfo.state}
                          onChange={handleInputChange}
                          placeholder="CO"
                          className={cn(
                            "mt-1.5",
                            errors.state && "border-destructive"
                          )}
                        />
                        {errors.state && (
                          <p className="text-xs text-destructive mt-1">{errors.state}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="zipCode" className="text-sm text-foreground">ZIP Code *</Label>
                        <Input
                          id="zipCode"
                          name="zipCode"
                          value={shippingInfo.zipCode}
                          onChange={handleInputChange}
                          placeholder="80202"
                          className={cn(
                            "mt-1.5",
                            errors.zipCode && "border-destructive"
                          )}
                        />
                        {errors.zipCode && (
                          <p className="text-xs text-destructive mt-1">{errors.zipCode}</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Column - Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg p-6 sticky top-8">
              <h2 className="text-lg font-display text-foreground mb-4">Order Summary</h2>
              
              {/* Items */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto scrollbar-gallery">
                {items.map((item) => (
                  <div key={item.itemKey} className="flex gap-3">
                    <div className="w-16 h-16 bg-muted rounded-sm overflow-hidden flex-shrink-0">
                      <img
                        src={item.selectedImage || item.product.image}
                        alt={item.product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {item.product.title}
                      </p>
                      <p className="text-xs text-muted-foreground">Qty: {item.quantity}</p>
                      <p className="text-sm text-foreground">
                        {(() => {
                          const p = item.unitPrice ?? item.product.price;
                          return p ? `$${(p * item.quantity).toFixed(2)}` : 'Inquire';
                        })()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Totals */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground">${subtotal.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Shipping
                    <span className="block text-xs">
                      ({deliveryMethod === 'pickup' ? 'Local Pickup' : 'Ship to Me'})
                    </span>
                  </span>
                  <span className={cn(
                    "text-foreground",
                    shippingCost === 0 && "text-green-600"
                  )}>
                    {shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Colorado Sales Tax ({(SALES_TAX_RATE * 100).toFixed(1)}%)
                  </span>
                  <span className="text-foreground">${taxAmount.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-xl font-medium pt-3 border-t border-border">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">${orderTotal.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Submit Order Button */}
              <Button
                onClick={handleSubmitOrder}
                disabled={isSubmitting}
                data-testid="submit-order-btn"
                className="w-full btn-purchase text-lg py-6 mt-6 group"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Submit Order'
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center mt-3">
                You'll be directed to payment after submitting
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
